import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import Copyable from '../../../components/utils/Copyable';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';

import {
  GetDemoApprovalsWT,
  DecideDemoApprovalWT
} from '../../../redux/actions/watchTower/certifyDemoApprovalsActions';

const DemoApprovalsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.watchTower);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [deciding, setDeciding] = useState(false);
  const [pending, setPending] = useState(null); // { domain, decision }

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = ["Id", "Name", "Email", "Domain", "Requested at", "Decision"];

  useEffect(() => {
    handleGetApprovals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    try {
      setList(state.demoCertifyApprovals.records || []);
      setTotal(state.demoCertifyApprovals.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetApprovals() {
    let query = "";
    if (search) query += `&search=${search}`;
    return dispatch(GetDemoApprovalsWT(page + 1, rowsPerPage, query, setLoading));
  };

  const openDecision = (domain, decision) => {
    setPending({ domain, decision });
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!pending) return;
    dispatch(DecideDemoApprovalWT({ domain: pending.domain, decision: pending.decision }, setDeciding))
      .then(() => {
        setConfirmOpen(false);
        setPending(null);
        handleGetApprovals();
      });
  };

  const confirmTitle = pending
    ? (pending.decision === 'allow'
        ? `Allow all requests from "${pending.domain}"?`
        : `Block "${pending.domain}"?`)
    : "";

  return (
    <Box>
      <ConfirmationModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={confirmTitle}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
        />

      <TopBar
        title={"Demo Access Approvals"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetApprovals();
        }}
        />

      <Typography
        variant='caption'
        sx={{ color: "grey", display: "block", mb: "14px" }}>
        Requests from domains we don&apos;t recognise yet. Allowing adds the domain to the
        allowlist; blocking adds it to the blocklist. The requester(s) and the team are
        notified either way.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          list.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No pending approval requests</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    <TableCell key={headingIndex}>{heading}</TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                list.map((row, index) => (
                  <TableRow
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name || <NA />}</TableCell>
                    <TableCell>{row.email || <NA />}</TableCell>
                    <TableCell>{row.domain || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.requestedAt}
                        />
                    </TableCell>
                    <TableCell>
                      <Box className="flexCenter_Row" gap={"10px"}>
                        <Button
                          size='small'
                          variant='contained'
                          startIcon={<CheckCircleOutlineIcon />}
                          onClick={() => openDecision(row.domain, 'allow')}
                          sx={{
                            minWidth: "unset",
                            minHeight: "unset",
                            padding: "5px 12px",
                            backgroundColor: "#2e7d32",
                            color: "#fff",
                            "&:hover": { backgroundColor: "#256a29" },
                          }}>
                          Allow
                        </Button>
                        <Button
                          size='small'
                          variant='outlined'
                          startIcon={<BlockIcon />}
                          onClick={() => openDecision(row.domain, 'deny')}
                          sx={{
                            minWidth: "unset",
                            minHeight: "unset",
                            padding: "5px 12px",
                            borderColor: "#f87171",
                            color: "#f87171",
                            "&:hover": { borderColor: "#f87171", backgroundColor: "rgba(248,113,113,0.12)" },
                          }}>
                          Deny
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default DemoApprovalsPage;
